import MirrorBackButton from "@/components/MirrorBackButton";
import MicrosoftTodoImportLauncher from "@/components/connections/MicrosoftTodoImportLauncher";
import { usePeopleTaskConnectionAccess } from "@/hooks/usePeopleTaskConnectionAccess";
import { PeopleTasksLauncher } from "@/components/mirror/PeopleTaskActionModal";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import MobileLayout from "@/components/MobileLayout";
import TaskModal from "@/components/TaskModal";
import {
  Plus, Star, ArrowRight, ChevronRight, ChevronLeft,
  Loader2, Circle, CheckCircle2, Pencil, Trash2, AlertCircle, RefreshCcw,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/i18n/LanguageProvider";
import { useTasks, isSyncedTask } from "@/hooks/useTasks";
import { usePendingTasks } from "@/lib/taskChanges";
import type { Priority, Task } from "@/hooks/useTasks";
import { SourceBadge, type DataSource } from "@/components/SourceBadge";
import ImportedTaskActionModal from "@/components/mirror/ImportedTaskActionModal";
import { snapshotForTask } from "@/lib/taskMutations";
import type { TaskSnapshot } from "@/lib/taskExecutor";
import { TaskPendingActionLauncher } from "@/components/mirror/TaskActionModal";

// ── Types ──────────────────────────────────────────────────────────────────

type Filter = "today" | "upcoming" | "all" | "completed";

// ── Helpers ────────────────────────────────────────────────────────────────


// ── Page ───────────────────────────────────────────────────────────────────

const Tasks: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t, tf } = useLanguage();
  const { microsoftTodoEnabled, userId: peopleOwner } = usePeopleTaskConnectionAccess();

  const {
    filtered,
    loading,
    error,
    fetchTasks,
    createTask,
    toggleTask,
    deleteTask,
    updateTask,
    userId,
  } = useTasks();

  const [filter, setFilter] = useState<Filter>("today");
  const [captureText, setCaptureText] = useState("");

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalOwner, setModalOwner] = useState<string | null>(null);
  const [modalInitialTitle, setModalInitialTitle] = useState("");
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [providerToggle, setProviderToggle] = useState<Task | null>(null);

  // Delete confirm state
  const [deleteTarget, setDeleteTarget] = useState<Task | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteSnapshot, setDeleteSnapshot] = useState<TaskSnapshot | null>(null);
  const reviewDelete = (task: Task) => { setDeleteSnapshot(snapshotForTask(task)); setDeleteTarget(structuredClone(task)); };

  // ── Filter config ────────────────────────────────────────────────────────

  const FILTERS: { key: Filter; label: string }[] = [
    { key: "today",     label: t("tasks.filters.today") },
    { key: "upcoming",  label: t("tasks.filters.upcoming") },
    { key: "all",       label: t("tasks.filters.all") },
    { key: "completed", label: t("tasks.filters.completed") },
  ];

  const visibleTasks = filtered[filter];

  // ── Priority config ──────────────────────────────────────────────────────

  const PRIORITY_STYLES: Record<Priority, { dot: string; bg: string; text: string; labelKey: string }> = {
    Critical:  { dot: "#ef4444", bg: "hsl(0 72% 51% / 0.15)",   text: "#f87171", labelKey: "tasks.priority.critical"  },
    Important: { dot: "#eab308", bg: "hsl(45 93% 47% / 0.15)",  text: "#facc15", labelKey: "tasks.priority.important" },
    Normal:    { dot: "#6b7280", bg: "hsl(220 9% 46% / 0.15)",  text: "#9ca3af", labelKey: "tasks.priority.normal"    },
  };

  const priorities: Priority[] = ["Critical", "Important", "Normal"];
  const tasksByPriority = (p: Priority) => visibleTasks.filter((t) => t.priority === p);

  // ── Insight logic ────────────────────────────────────────────────────────

  const criticalOpen = filtered.all.filter((t) => t.priority === "Critical").length;
  const importantOpen = filtered.all.filter((t) => t.priority === "Important").length;

  const insightText = (() => {
    if (criticalOpen > 0)
      return tf("tasks.insight.critical_open", { count: criticalOpen });
    if (importantOpen > 0)
      return tf("tasks.insight.important_open", { count: importantOpen });
    if (filtered.all.length > 0) return tf("ux.tasks.openCount", { count: filtered.all.length });
    return t("tasks.insight.all_done");
  })();

  // ── Handlers ─────────────────────────────────────────────────────────────

  const openNewModal = () => {
    setModalOwner(userId ?? null);
    setEditingTask(null);
    setModalInitialTitle(captureText.trim());
    setModalOpen(true);
  };

  const handleCapture = () => {
    const text = captureText.trim();
    if (!text) return;
    setModalOwner(userId ?? null);
    setEditingTask(null);
    setModalInitialTitle(text);
    setModalOpen(true);
  };

  const handleEditTask = (task: Task) => {
    setModalOwner(task.user_id);
    setEditingTask(task);
    setModalInitialTitle("");
    setModalOpen(true);
  };

  const handleToggle = async (id: string) => {
    const task = visibleTasks.find(task => task.id === id);
    if (task && isSyncedTask(task)) { setProviderToggle(task); return; }
    try { const expected = task && snapshotForTask(task); if (!expected) throw new Error("invalid_snapshot"); await toggleTask(id, expected); }
    catch { toast({ description: t("ux.tasks.mutationError"), variant: "destructive" }); }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      if (!deleteSnapshot) throw new Error("invalid_snapshot");
      await deleteTask(deleteTarget.id, deleteSnapshot);
      setDeleteTarget(null);
    } catch (e) {
      const blocked = e instanceof Error && e.message === "SYNCED_TASK_DELETE_BLOCKED";
      toast({
        description: t(blocked ? "tasks.deleteSyncedBlocked" : "tasks.deleteError"),
        duration: 2600,
      });
    } finally {
      setDeleting(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <MobileLayout className="pb-28 pt-8 relative overflow-hidden">
      {providerToggle && <ImportedTaskActionModal open task={providerToggle}
        action={providerToggle.done ? "reopen" : "complete"}
        onClose={() => setProviderToggle(null)} onCreated={() => { void fetchTasks(true); }} />}
      {/* Ambient glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[380px] h-[220px] rounded-full opacity-[0.04]"
          style={{ background: "radial-gradient(ellipse 55% 50% at 50% 50%, hsl(187 72% 50%), transparent)" }}
        />
      </div>

      {/* BACK BUTTON */}
      <MirrorBackButton className="mb-4" />

      {/* HEADER */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex items-center justify-between mb-5"
      >
        <div>
          <p
            className="text-[9px] font-display tracking-[0.2em] uppercase mb-0.5"
            style={{ color: "hsl(187 72% 50% / 0.55)" }}
          >
            {t("tasks.yourFocus")}
          </p>
          <h1 className="font-display text-[26px] font-semibold text-foreground leading-none">
            {t("tasks.title")}
          </h1>
        </div>
        <button
          onClick={openNewModal}
          className="min-h-11 px-3 gap-2 rounded-xl flex items-center justify-center text-sm text-primary font-medium focus-visible:ring-2 focus-visible:ring-primary"
          style={{
            background: "hsl(187 72% 50% / 0.10)",
            border: "1px solid hsl(187 72% 50% / 0.20)",
          }}
        >
          <Plus size={16} aria-hidden="true" />{t("ux.tasks.new")}
        </button>
      </motion.div>

      <PeopleTasksLauncher kind="task" />
      <MicrosoftTodoImportLauncher enabled={microsoftTodoEnabled && peopleOwner === userId} ownerId={userId ?? null} className="mb-4" />
      {userId && <TaskPendingActionLauncher ownerId={userId} busy={deleting || modalOpen} onMirrorResume={locator => navigate("/mirror", { state: { taskActionRecovery: locator } })} />}

      {/* CAPTURE BAR */}
      <form onSubmit={(e) => { e.preventDefault(); handleCapture(); }}
        className="mb-5 rounded-2xl border border-primary/20 bg-card p-4 space-y-3">
        <label htmlFor="task-capture" className="block text-sm font-medium text-foreground">
          {t("ux.tasks.capture")}
        </label>
        <input id="task-capture" value={captureText}
          onChange={(e) => setCaptureText(e.target.value)}
          placeholder={t("tasks.capture_placeholder")}
          className="w-full min-h-11 rounded-xl border border-border bg-background/60 px-3 text-base text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary" />
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-muted-foreground">{t("ux.tasks.captureHint")}</p>
          <button type="submit" disabled={!captureText.trim()}
            className="min-h-11 px-4 rounded-xl bg-primary/15 border border-primary/30 text-primary text-sm font-medium disabled:opacity-40 focus-visible:ring-2 focus-visible:ring-primary">
            {t("ux.tasks.continue")}
          </button>
        </div>
      </form>

      {/* FILTERS */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1, duration: 0.5 }}
        className="flex gap-2 mb-5 overflow-x-auto pb-1 scrollbar-none"
        style={{ scrollbarWidth: "none" }}
      >
        {FILTERS.map(({ key, label }) => {
          const active = filter === key;
          return (
            <button
              key={key}
              onClick={() => setFilter(key)}
              aria-pressed={active}
              className="shrink-0 min-h-11 px-4 rounded-full font-display text-sm focus-visible:ring-2 focus-visible:ring-primary"
              style={
                active
                  ? { background: "hsl(var(--primary))", color: "hsl(var(--background))", fontWeight: 600 }
                  : {
                      background: "hsl(187 72% 50% / 0.07)",
                      border: "1px solid hsl(187 72% 50% / 0.15)",
                      color: "hsl(210 20% 60%)",
                    }
              }
            >
              {label}
            </button>
          );
        })}
      </motion.div>

      {/* ERROR STATE */}
      {error && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="rounded-2xl p-4 mb-5 flex items-center gap-3"
          style={{ background: "hsl(0 72% 51% / 0.08)", border: "1px solid hsl(0 72% 51% / 0.25)" }}
        >
          <AlertCircle size={16} style={{ color: "hsl(0 72% 60%)" }} className="shrink-0" />
          <p className="text-[12px] font-body flex-1" style={{ color: "hsl(0 72% 70%)" }}>{error}</p>
          <button
            onClick={() => fetchTasks()}
            aria-label={t("ux.notes.retry")}
            className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: "hsl(0 72% 51% / 0.12)" }}
          >
            <RefreshCcw size={12} style={{ color: "hsl(0 72% 60%)" }} />
          </button>
        </motion.div>
      )}

      {/* MIRROR INSIGHT CARD */}
      {filter !== "completed" && !loading && !error && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.14, duration: 0.5 }}
          className="rounded-2xl p-4 mb-5 relative overflow-hidden"
          style={{
            background: "linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.65)), url(https://lluvykhqgdnbfobjixbj.supabase.co/storage/v1/object/public/mirror-brief/room%20ex_017.jpeg) center/cover no-repeat",
            border: "1px solid hsl(187 72% 50% / 0.22)",
          }}
        >
          <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-primary/25 to-transparent" />
          <div className="flex items-start gap-3">
            <div
              className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0 mt-0.5"
              style={{ background: "hsl(187 72% 50% / 0.12)", border: "1px solid hsl(187 72% 50% / 0.20)" }}
            >
              <Star size={14} style={{ color: "hsl(187 72% 55%)" }} />
            </div>
            <div className="flex-1 min-w-0">
              <p
                className="text-[9px] font-display tracking-[0.18em] uppercase mb-1"
                style={{ color: "hsl(187 72% 50% / 0.55)" }}
              >
                {t("tasks.insight.title")}
              </p>
              <p className="text-[12px] font-body text-foreground/65 leading-relaxed mb-3">
                {insightText}
              </p>
              <button
                onClick={() => navigate("/mirror")}
                className="flex items-center gap-1 font-display text-[11px] font-medium tracking-wide transition-opacity hover:opacity-100 opacity-75"
                style={{ color: "hsl(187 72% 55%)" }}
              >
                <span>{t("tasks.insight.cta")}</span>
                <ArrowRight size={11} />
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {/* TASK LIST */}
      {loading ? (
        /* ── Skeleton loaders ── */
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className="rounded-2xl p-4 animate-pulse"
              style={{ background: "hsl(var(--card))", border: "1px solid hsl(187 72% 50% / 0.12)" }}
            >
              <div className="flex items-center gap-3">
                <div className="w-4 h-4 rounded-full shrink-0" style={{ background: "hsl(218 14% 20%)" }} />
                <div className="flex-1 space-y-2">
                  <div className="h-3 rounded-full w-3/4" style={{ background: "hsl(218 14% 20%)" }} />
                  <div className="h-2 rounded-full w-1/3" style={{ background: "hsl(218 14% 15%)" }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : filter === "completed" ? (
        /* ── Completed view ── */
        visibleTasks.length === 0 && !error ? (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.16 }}
            className="flex flex-col items-center justify-center text-center py-16 px-6"
          >
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4"
              style={{ background: "hsl(187 72% 50% / 0.08)", border: "1px solid hsl(187 72% 50% / 0.18)" }}
            >
              <CheckCircle2 size={22} style={{ color: "hsl(187 72% 50% / 0.50)" }} />
            </div>
            <p className="font-display text-[14px] font-medium leading-snug mb-4 whitespace-pre-line"
              style={{ color: "hsl(210 20% 55%)" }}
            >
              {t("tasks.empty.completed")}
            </p>
            <button
              onClick={() => navigate("/mirror")}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-display text-[12px] font-medium tracking-wide transition-all duration-200 active:scale-95"
              style={{
                background: "hsl(187 72% 50% / 0.12)",
                border: "1px solid hsl(187 72% 50% / 0.25)",
                color: "hsl(187 72% 55%)",
              }}
            >
              {t("tasks.open_mirror")}
              <ChevronRight size={13} />
            </button>
          </motion.div>
        ) : (
          <AnimatePresence>
            <div className="space-y-2">
              {visibleTasks.map((task, i) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  index={i}
                  onToggle={handleToggle}
                  onMirror={() => navigate("/mirror")}
                  onEdit={() => handleEditTask(task)}
                  onDelete={() => reviewDelete(task)}
                  t={t}
                />
              ))}
            </div>
          </AnimatePresence>
        )
      ) : (
        /* ── Priority groups ── */
        <div>
          <AnimatePresence>
            {priorities.map((p) => {
              const group = tasksByPriority(p);
              if (group.length === 0) return null;
              const style = PRIORITY_STYLES[p];
              return (
                <motion.div
                  key={p}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.18 }}
                  className="mb-5"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-2 h-2 rounded-full" style={{ background: style.dot }} />
                    <p className="text-[10px] font-display tracking-[0.18em] uppercase" style={{ color: style.text }}>
                      {t(style.labelKey)}
                    </p>
                  </div>
                  <div className="space-y-2">
                    {group.map((task, i) => (
                      <TaskCard
                        key={task.id}
                        task={task}
                        index={i}
                        onToggle={handleToggle}
                        onMirror={() => navigate("/mirror")}
                        onEdit={() => handleEditTask(task)}
                        onDelete={() => reviewDelete(task)}
                        t={t}
                      />
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>

          {visibleTasks.length === 0 && !error && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-12"
            >
              <p className="font-display text-[13px]" style={{ color: "hsl(210 20% 45%)" }}>
                {t("tasks.empty.no_tasks")}
              </p>
            </motion.div>
          )}
        </div>
      )}


      {/* CREATE / EDIT MODAL */}
      <TaskModal
        open={modalOpen && modalOwner === userId}
        initialTitle={modalInitialTitle}
        task={editingTask}
        onClose={() => { setModalOpen(false); setEditingTask(null); }}
        onCreate={async (input) => {
          const created = await createTask(input);
          if (!created) throw new Error("Task creation was not confirmed");
          setCaptureText("");
          setFilter("all");
          toast({ description: t("ux.tasks.created") });
          return created;
        }}
        onUpdate={updateTask}
        onRefreshReview={() => void fetchTasks()}
      />

      {/* DELETE CONFIRM SHEET */}
      <AnimatePresence>
        {deleteTarget && (
          <>
            <motion.div
              key="del-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-black/60"
              onClick={() => { if (!deleting) setDeleteTarget(null); }}
            />
            <motion.div
              key="del-sheet"
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 28, stiffness: 300 }}
              className="fixed bottom-0 left-0 right-0 z-50 rounded-t-3xl px-5 pt-5 pb-10"
              style={{
                background: "#10172a",
                border: "1px solid hsl(0 72% 51% / 0.25)",
                borderBottom: "none",
              }}
            >
              <div className="flex justify-center mb-5">
                <div className="w-10 h-1 rounded-full" style={{ background: "hsl(218 14% 30%)" }} />
              </div>
              <p className="text-[14px] font-display font-medium text-foreground mb-6 text-center">
                {t("tasks.delete.confirm_message")}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteTarget(null)}
                  disabled={deleting}
                  className="flex-1 py-3 rounded-xl font-display text-[13px] font-medium tracking-wide transition-all active:scale-95 disabled:opacity-50"
                  style={{
                    background: "hsl(218 14% 12%)",
                    border: "1px solid hsl(218 14% 20%)",
                    color: "hsl(218 14% 60%)",
                  }}
                >
                  {t("tasks.modal.cancel")}
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  disabled={deleting || !deleteSnapshot}
                  className="flex-1 py-3 rounded-xl font-display text-[13px] font-semibold tracking-wide transition-all active:scale-95 disabled:opacity-50"
                  style={{
                    background: "hsl(0 72% 51% / 0.85)",
                    color: "#fff",
                  }}
                >
                  {deleting ? "…" : t("tasks.delete.confirm_button")}
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </MobileLayout>
  );
};

// ── TaskCard ───────────────────────────────────────────────────────────────

interface TaskCardProps {
  task: Task;
  index: number;
  onToggle: (id: string) => void;
  onMirror: () => void;
  onEdit: () => void;
  onDelete: () => void;
  t: (key: string) => string;
}

const PRIORITY_CARD_STYLES: Record<Priority, { bg: string; text: string }> = {
  Critical:  { bg: "hsl(0 72% 51% / 0.15)",   text: "#f87171" },
  Important: { bg: "hsl(45 93% 47% / 0.15)",  text: "#facc15" },
  Normal:    { bg: "hsl(220 9% 46% / 0.15)",  text: "#9ca3af" },
};

const TaskCard: React.FC<TaskCardProps> = ({ task, index, onToggle, onMirror, onEdit, onDelete, t }) => {
  const style = PRIORITY_CARD_STYLES[task.priority];
  const pending = usePendingTasks().has(task.id);

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -20, transition: { duration: 0.25 } }}
      transition={{ delay: 0.04 * index }}
      layout
      className="rounded-2xl p-4 relative overflow-hidden"
      style={{
        background: "hsl(var(--card))",
        border: "1px solid hsl(187 72% 50% / 0.20)",
        opacity: task.done ? 0.55 : 1,
      }}
    >
      <div className="flex items-start gap-3">
        {/* Checkbox */}
        <button
          onClick={() => onToggle(task.id)}
          disabled={pending}
          aria-label={t(task.done ? "ux.tasks.reopen" : "common.done")}
          aria-busy={pending}
          className="shrink-0 mt-0.5 transition-colors"
        >
          {pending ? <Loader2 size={17} className="animate-spin" /> : task.done ? (
            <CheckCircle2 size={17} style={{ color: "hsl(187 72% 50% / 0.70)" }} />
          ) : (
            <Circle size={17} style={{ color: "hsl(210 20% 35%)" }} />
          )}
        </button>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <p
              className={`font-display text-[13px] font-semibold leading-snug transition-all ${
                task.done ? "line-through text-foreground/30" : "text-foreground"
              }`}
            >
              {task.title}
            </p>
            <span
              className="text-[9px] font-display tracking-[0.12em] uppercase px-2 py-0.5 rounded-full shrink-0"
              style={{ background: style.bg, color: style.text }}
            >
              {t(`tasks.priority.display.${task.priority}`)}
            </span>
            {isSyncedTask(task) && (
              <SourceBadge source={task.source as DataSource} />
            )}
          </div>

          {task.time_label && (
            <p className="text-[10px] font-body mb-2" style={{ color: "hsl(218 14% 50%)" }}>
              {task.time_label}
            </p>
          )}

          {!task.done && (
            <button
              onClick={onMirror}
              className="flex items-center gap-1 font-display text-[10px] tracking-wide transition-opacity hover:opacity-100 opacity-60"
              style={{ color: "hsl(187 72% 55%)" }}
            >
              <span>{t("tasks.ask_mirror")}</span>
              <ArrowRight size={9} />
            </button>
          )}
        </div>

        {/* Edit / Delete */}
        <div className="flex flex-col gap-2 shrink-0">
          <button
            onClick={onEdit}
            disabled={pending}
            aria-label={t("tasks.modal.edit_title")}
            className="w-7 h-7 rounded-lg flex items-center justify-center transition-opacity active:opacity-60"
            style={{ background: "hsl(187 72% 50% / 0.08)" }}
          >
            <Pencil size={11} style={{ color: "hsl(187 72% 50% / 0.70)" }} />
          </button>
          {/* Rule 30/06: provider-synced tasks (google_tasks) get no delete —
              tasks has no tombstone, the next pull would resurrect the row. */}
          {!isSyncedTask(task) && (
            <button
              onClick={onDelete}
              disabled={pending}
              aria-label={t("tasks.delete.confirm_button")}
              className="w-7 h-7 rounded-lg flex items-center justify-center transition-opacity active:opacity-60"
              style={{ background: "hsl(0 72% 51% / 0.08)" }}
            >
              <Trash2 size={11} style={{ color: "hsl(0 72% 60% / 0.70)" }} />
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default Tasks;
